"use client";

import type React from "react";
import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  Textarea,
  VStack,
  Text,
  Flex,
  useToast,
  Divider,
  Checkbox,
  IconButton,
  ModalOverlay,
  Input,
  ModalHeader,
  FormControl,
  FormLabel,
  HStack,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  RadioGroup,
  Radio,
  Tooltip,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
} from "@chakra-ui/react";
import { EditIcon } from "@chakra-ui/icons";
import { Trash2, ChevronDown, Info } from "lucide-react";
import { VscCallOutgoing } from "react-icons/vsc";
import type { Node } from "./types";

interface NodePropertiesDialogProps {
  node: Node;
  onClose: () => void;
  onUpdate: (updatedNode: Node, originalNodeId: string) => void;
  onDelete: (nodeId: string) => void;
  onSetAsStarting: (nodeId: string) => void;
  isStartingNode: boolean;
  agentId?: string;
  onDuplicate?: (node: Node) => void;
}

export const NodePropertiesDialog: React.FC<NodePropertiesDialogProps> = ({
  node,
  onClose,
  onUpdate,
  onDelete,
  onSetAsStarting,
  isStartingNode,
  agentId,
  onDuplicate,
}) => {
  const [stateName, setStateName] = useState(
    node.data?.state_name || node.id || ""
  );
  const [statePrompt, setStatePrompt] = useState(node.data?.state_prompt || "");
  const [enableInterruptionSensitivity, setEnableInterruptionSensitivity] =
    useState(false);
  const [interruptionSensitivityValue, setInterruptionSensitivityValue] =
    useState(1);
  const [isEditing, setIsEditing] = useState(false);
  const [stateNameError, setStateNameError] = useState("");
  const [toolNameErrors, setToolNameErrors] = useState<Record<number, string>>(
    {}
  );
  const [tools, setTools] = useState<any[]>([]);
  const [jsonSchemaError, setJsonSchemaError] = useState<
    Record<number, string>
  >({});

  const toast = useToast();

  // Palette driven by Chakra + your Dashboard file
  const surfaceBg = "white";
  const textColor = "gray.800";
  const subText = "gray.600";
  const softText = "gray.500";
  const borderColor = "gray.200";
  const subtleBg = "gray.50";
  const inputBg = "white";
  const menuBg = "white";

  // Store the original node ID for reference
  const originalNodeId = node.id;

  // Update local state if node props change
  useEffect(() => {
    setStateName(node.data?.state_name || node.id || "");
    setStatePrompt(node.data?.state_prompt || "");
  }, [node]);

  useEffect(() => {
    setEnableInterruptionSensitivity(!!node.data?.interruption_sensitivity);

    if (
      typeof node.data?.interruption_sensitivity === "number" &&
      node.data?.interruption_sensitivity >= 0 &&
      node.data?.interruption_sensitivity <= 1
    ) {
      setInterruptionSensitivityValue(node.data.interruption_sensitivity);
    } else if (!!node.data?.interruption_sensitivity) {
      setInterruptionSensitivityValue(1);
    }
  }, [node.data?.interruption_sensitivity]);

  // Initialize tools from node data
  useEffect(() => {
    if (node.data?.tools && Array.isArray(node.data.tools)) {
      const initializedTools = JSON.parse(JSON.stringify(node.data.tools));

      initializedTools.forEach((tool: any) => {
        if (
          tool.type === "custom" &&
          tool.parameters &&
          typeof tool.parameters === "object"
        ) {
          tool.parameters = JSON.stringify(tool.parameters, null, 2);
        }

        if (tool.type === "transfer_call" && tool.custom_sip_headers) {
          const headers = Object.entries(tool.custom_sip_headers).map(
            ([key, value]) => ({
              key,
              value: value as string,
              error: validateSipHeaderKey(key),
            })
          );
          tool.custom_sip_headers = headers;
        }

        if (tool.type === "transfer_call" && tool.transfer_option) {
          if (tool.transfer_option.type === "cold_transfer") {
            tool.transferType = "cold";
            tool.displayedPhoneNumber = tool.transfer_option
              .show_transferee_as_caller
              ? "transferee"
              : "retell";
          } else if (tool.transfer_option.type === "warm_transfer") {
            tool.transferType = "warm";
            if (tool.transfer_option.public_handoff_option) {
              tool.warmTransferOptionType =
                tool.transfer_option.public_handoff_option.type;
              if (
                tool.transfer_option.public_handoff_option.type ===
                "static_message"
              ) {
                tool.warmTransferMessage =
                  tool.transfer_option.public_handoff_option.message;
              } else if (
                tool.transfer_option.public_handoff_option.type === "prompt"
              ) {
                tool.warmTransferPrompt =
                  tool.transfer_option.public_handoff_option.prompt;
              }
            }
          }
        }
      });

      setTools(initializedTools);
    } else {
      setTools([]);
    }
  }, [node.data?.tools]);

  const validateStateName = (name: string) => {
    if (!name.trim()) {
      setStateNameError("State name cannot be empty");
      return false;
    }
    if (name.length > 64) {
      setStateNameError("State name must be 64 characters or less");
      return false;
    }
    const validNameRegex = /^[a-zA-Z0-9_-]+$/;
    if (!validNameRegex.test(name)) {
      setStateNameError(
        "State name must only contain letters, numbers, underscores, and dashes (no spaces)"
      );
      return false;
    }
    setStateNameError("");
    return true;
  };

  const validateToolName = (name: string, index: number) => {
    const validNameRegex = /^[a-zA-Z0-9_-]+$/;
    if (!validNameRegex.test(name)) {
      setToolNameErrors((prev) => ({
        ...prev,
        [index]:
          "No spaces allowed. Allowed characters: a-z, A-Z, 0-9, _ and -.",
      }));
      return false;
    }
    setToolNameErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[index];
      return newErrors;
    });
    return true;
  };

  const validateJsonSchema = (schema: string, index: number) => {
    if (!schema.trim()) {
      setJsonSchemaError((prev) => {
        const newErrors = { ...prev };
        delete newErrors[index];
        return newErrors;
      });
      return true;
    }

    try {
      const parsedSchema = JSON.parse(schema);
      if (parsedSchema.type !== "object") {
        setJsonSchemaError((prev) => ({
          ...prev,
          [index]: "Schema root type must be 'object'",
        }));
        return false;
      }
      setJsonSchemaError((prev) => {
        const newErrors = { ...prev };
        delete newErrors[index];
        return newErrors;
      });
      return true;
    } catch {
      setJsonSchemaError((prev) => ({
        ...prev,
        [index]: "Invalid JSON format",
      }));
      return false;
    }
  };

  const validateSipHeaderKey = (key: string) => {
    if (!key.startsWith("X-") && !key.startsWith("x-")) {
      return `Custom SIP header keys must start with X- for key: ${key}`;
    }
    return "";
  };

  const handleAddSipHeader = (toolIndex: number) => {
    const updatedTools = [...tools];
    if (!updatedTools[toolIndex].custom_sip_headers) {
      updatedTools[toolIndex].custom_sip_headers = [];
    }
    updatedTools[toolIndex].custom_sip_headers.push({
      key: "",
      value: "",
      error: "",
    });
    setTools(updatedTools);
  };

  const handleUpdateSipHeader = (
    toolIndex: number,
    headerIndex: number,
    field: "key" | "value",
    value: string
  ) => {
    const updatedTools = [...tools];
    if (!updatedTools[toolIndex].custom_sip_headers) {
      updatedTools[toolIndex].custom_sip_headers = [];
    }
    updatedTools[toolIndex].custom_sip_headers[headerIndex][field] = value;
    if (field === "key") {
      updatedTools[toolIndex].custom_sip_headers[headerIndex].error =
        validateSipHeaderKey(value);
    }
    setTools(updatedTools);
  };

  const handleRemoveSipHeader = (toolIndex: number, headerIndex: number) => {
    const updatedTools = [...tools];
    if (updatedTools[toolIndex].custom_sip_headers) {
      updatedTools[toolIndex].custom_sip_headers.splice(headerIndex, 1);
      setTools(updatedTools);
    }
  };

  const handleStateNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    setStateName(newName);
    validateStateName(newName);
  };

  const formatJsonSchema = (index: number) => {
    try {
      const tool = tools[index];
      if (tool.parameters && typeof tool.parameters === "string") {
        const formatted = JSON.stringify(JSON.parse(tool.parameters), null, 2);
        handleUpdateTool(index, "parameters", formatted);
      }
    } catch {
      // ignore
    }
  };

  const exampleSchemas = [
    {
      type: "object",
      properties: {
        name: { type: "string", description: "The name of the person" },
        age: { type: "integer", description: "The age of the person" },
      },
      required: ["name"],
    },
    {
      type: "object",
      properties: {
        query: { type: "string", description: "Search query" },
        limit: {
          type: "integer",
          description: "Maximum number of results to return",
        },
      },
      required: ["query"],
    },
    {
      type: "object",
      properties: {
        product_id: { type: "string", description: "The ID of the product" },
        quantity: { type: "integer", description: "The quantity to order" },
        options: {
          type: "object",
          properties: {
            color: { type: "string", description: "Product color" },
            size: { type: "string", description: "Product size" },
          },
        },
      },
      required: ["product_id", "quantity"],
    },
  ];

  const handleAddTool = (toolType: string) => {
    let newTool: any = {
      type: toolType,
      name: `${toolType}_${tools.length + 1}`,
      description: "",
    };

    if (toolType === "transfer_call") {
      newTool = {
        ...newTool,
        transfer_destination: { type: "predefined", number: "+12134447777" },
        transfer_option: {
          type: "cold_transfer",
          show_transferee_as_caller: false,
        },
      };
    } else if (toolType === "press_digit") {
      newTool.delay_ms = 1000;
    } else if (
      toolType === "check_availability_cal" ||
      toolType === "book_appointment_cal"
    ) {
      newTool.cal_api_key = "";
      newTool.event_type_id = 0;
      newTool.timezone = "America/Los_Angeles";
    } else if (toolType === "custom") {
      newTool = {
        ...newTool,
        url: "",
        description: "",
        speak_during_execution: true,
        speak_after_execution: true,
        execution_message_description:
          "The message you will say to user when calling this tool. Make sure it fits into the conversation smoothly.",
        timeout_ms: 120000,
        parameters: "",
      };
    }

    setTools([...tools, newTool]);
  };

  const handleUpdateTool = (index: number, field: string, value: any) => {
    const updatedTools = [...tools];
    let current = updatedTools[index];

    if (field === "transfer_option.type") {
      if (value === "cold_transfer") {
        current.transfer_option = {
          type: "cold_transfer",
          show_transferee_as_caller: false,
        };
      } else if (value === "warm_transfer") {
        current.transfer_option = {
          type: "warm_transfer",
          public_handoff_option: { type: "static_message", message: "" },
        };
      }
    } else if (field.includes(".")) {
      const parts = field.split(".");
      for (let i = 0; i < parts.length - 1; i++) {
        const part = parts[i];
        if (!current[part]) current[part] = {};
        current = current[part];
      }
      current[parts[parts.length - 1]] = value;
    } else {
      current[field] = value;
    }

    if (field === "name") validateToolName(value, index);
    if (field === "parameters" && updatedTools[index].type === "custom") {
      validateJsonSchema(value, index);
    }

    setTools(updatedTools);
  };

  const handleDeleteTool = (index: number) => {
    const updatedTools = [...tools];
    updatedTools.splice(index, 1);
    setTools(updatedTools);
  };

  const handleSave = () => {
    if (!validateStateName(stateName)) {
      toast({
        title: "Invalid state name",
        description: stateNameError,
        status: "error",
        duration: 3000,
      });
      return;
    }

    let hasToolNameError = false;
    tools.forEach((tool, index) => {
      if (!validateToolName(tool.name, index)) hasToolNameError = true;
    });
    if (hasToolNameError) {
      toast({
        title: "Invalid tool name",
        description:
          "One or more tools have invalid names. Tool names must only contain letters, numbers, underscores, and dashes (no spaces).",
        status: "error",
        duration: 3000,
      });
      return;
    }

    let hasJsonSchemaError = false;
    tools.forEach((tool, index) => {
      if (
        tool.type === "custom" &&
        tool.parameters &&
        !validateJsonSchema(tool.parameters, index)
      ) {
        hasJsonSchemaError = true;
      }
    });
    if (hasJsonSchemaError) {
      toast({
        title: "Invalid JSON schema",
        description:
          "One or more custom tools have invalid JSON schema parameters.",
        status: "error",
        duration: 3000,
      });
      return;
    }

    let hasInvalidSipHeaders = false;
    tools.forEach((tool) => {
      if (tool.type === "transfer_call" && tool.custom_sip_headers) {
        const hasErrors = tool.custom_sip_headers.some(
          (header: any) => header.key && validateSipHeaderKey(header.key)
        );
        if (hasErrors) hasInvalidSipHeaders = true;
      }
    });
    if (hasInvalidSipHeaders) {
      toast({
        title: "Invalid SIP header keys",
        description: "All SIP header keys must start with X-",
        status: "error",
        duration: 3000,
      });
      return;
    }

    const formattedTools = tools.map((tool) => {
      if (tool.type === "custom" && tool.parameters) {
        try {
          if (typeof tool.parameters === "string" && tool.parameters.trim()) {
            return { ...tool, parameters: JSON.parse(tool.parameters) };
          }
          if (typeof tool.parameters === "string" && !tool.parameters.trim()) {
            const { parameters, ...rest } = tool;
            return rest;
          }
        } catch {
          /* keep as-is */
        }
      }

      if (tool.type === "transfer_call" && tool.custom_sip_headers) {
        const sipHeadersObject = tool.custom_sip_headers
          .filter((h: any) => h.key && h.value)
          .reduce((acc: Record<string, string>, h: any) => {
            acc[h.key] = h.value;
            return acc;
          }, {});
        const { custom_sip_headers, ...toolWithoutHeaders } = tool;

        if (Object.keys(sipHeadersObject).length > 0) {
          toolWithoutHeaders.custom_sip_headers = sipHeadersObject;
        } else {
          delete toolWithoutHeaders.custom_sip_headers;
        }
        const {
          transferType,
          displayedPhoneNumber,
          warmTransferOptionType,
          warmTransferMessage,
          warmTransferPrompt,
          ...restOfTool
        } = toolWithoutHeaders;
        return restOfTool;
      }

      if (tool.type === "transfer_call") {
        const {
          transferType,
          displayedPhoneNumber,
          warmTransferOptionType,
          warmTransferMessage,
          warmTransferPrompt,
          ...restOfTool
        } = tool;
        return restOfTool;
      }

      return tool;
    });

    const updatedData: any = {
      ...node.data,
      state_name: stateName,
      state_prompt: statePrompt,
      tools: formattedTools,
    };

    if (enableInterruptionSensitivity) {
      updatedData.interruption_sensitivity = interruptionSensitivityValue;
    } else {
      delete updatedData.interruption_sensitivity;
    }

    const updatedNode: Node = {
      ...node,
      id: stateName,
      data: updatedData,
    };

    onUpdate(updatedNode, originalNodeId);
    onClose();
  };

  const sharedInputProps = {
    bg: inputBg,
    _placeholder: { color: softText },
  };

  return (
    <Modal isOpen={true} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent
        bg={surfaceBg}
        color={textColor}
        maxW="400px"
        borderColor={borderColor}
      >
        <ModalHeader p={0}>
          <Flex justify="space-between" align="center" p={4} pb={2}>
            <Flex align="center" gap={2}>
              {isEditing ? (
                <Box position="relative">
                  <Input
                    value={stateName}
                    onChange={handleStateNameChange}
                    onBlur={() => {
                      if (validateStateName(stateName)) setIsEditing(false);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && validateStateName(stateName)) {
                        setIsEditing(false);
                      }
                    }}
                    autoFocus
                    size="lg"
                    width="auto"
                    height="auto"
                    fontWeight="medium"
                    variant="filled"
                    borderColor="transparent"
                    outline="none"
                    {...sharedInputProps}
                  />
                  {stateNameError && (
                    <Text
                      color="red.500"
                      fontSize="xs"
                      position="absolute"
                      top="100%"
                      left="0"
                    >
                      {stateNameError}
                    </Text>
                  )}
                </Box>
              ) : (
                <>
                  <Text
                    fontSize="lg"
                    fontWeight="medium"
                    onClick={() => setIsEditing(true)}
                    cursor="pointer"
                  >
                    {stateName}
                  </Text>
                  <IconButton
                    aria-label="Edit state name"
                    icon={<EditIcon />}
                    size="xs"
                    variant="ghost"
                    onClick={() => setIsEditing(true)}
                    ml={1}
                  />
                </>
              )}
            </Flex>
            <Flex gap={2}>
              <IconButton
                aria-label="delete"
                icon={<Trash2 size={16} />}
                size="lg"
                variant="ghost"
                onClick={() => {
                  onDelete(node.id);
                  onClose();
                }}
              />
              {onDuplicate && (
                <IconButton
                  aria-label="duplicate"
                  icon={
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect width="14" height="14" x="8" y="8" rx="2" />
                      <path d="M4 8v10a2 2 0 0 0 2 2h10" />
                    </svg>
                  }
                  size="lg"
                  variant="ghost"
                  onClick={() => {
                    onDuplicate(node);
                    onClose();
                  }}
                />
              )}
            </Flex>
          </Flex>
          <Divider borderColor={borderColor} />
          <Box p={4}>
            {!isStartingNode && (
              <Flex
                align="center"
                gap={2}
                onClick={() => onSetAsStarting(node.id)}
                cursor="pointer"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    d="M15.8332 10.8337H10.8332V15.8337H9.1665V10.8337H4.1665V9.16699H9.1665V4.16699H10.8332V9.16699H15.8332V10.8337Z"
                    fill="currentColor"
                  />
                </svg>
                <Text fontSize="sm" fontWeight="medium">
                  Set as a starting state
                </Text>
              </Flex>
            )}
            {isStartingNode && (
              <Text fontSize="sm" fontWeight="medium" color={subText}>
                This is the starting state
              </Text>
            )}
          </Box>
        </ModalHeader>

        <ModalBody p={4}>
          <VStack spacing={4} align="stretch">
            <Box>
              <Text fontSize="md" fontWeight="medium" mb={2}>
                Prompt
              </Text>
              <Textarea
                value={statePrompt}
                onChange={(e) => setStatePrompt(e.target.value)}
                variant="outline"
                resize="none"
                fontSize="sm"
                minHeight="200px"
                size="lg"
                pr="100px"
                placeholder={`Enter part of your prompt here, for example, 'List a series of questions to ask the user:
[Question 1]
[Question 2]
[Question 3]
[Question 4]' `}
                {...sharedInputProps}
                color={textColor}
                borderColor={borderColor}
                _hover={{ borderColor }}
                _focus={{
                  borderColor: "primary.500",
                  boxShadow: "0 0 0 1px var(--chakra-colors-primary-500)",
                }}
              />
            </Box>

            <Box>
              <HStack justify="space-between" mb={2}>
                <Text fontSize="md" fontWeight="medium">
                  Tools (Optional)
                </Text>
                <Menu>
                  <MenuButton
                    as={Button}
                    rightIcon={<ChevronDown size={16} />}
                    size="sm"
                    variant="outline"
                    borderColor={borderColor}
                  >
                    Add Tool
                  </MenuButton>
                  <MenuList bg={menuBg} borderColor={borderColor}>
                    <MenuItem onClick={() => handleAddTool("end_call")}>
                      End Call
                    </MenuItem>
                    <MenuItem onClick={() => handleAddTool("transfer_call")}>
                      Call Transfer
                    </MenuItem>
                    <MenuItem onClick={() => handleAddTool("press_digit")}>
                      Press Digit (IVR Navigation)
                    </MenuItem>
                    <MenuItem
                      onClick={() => handleAddTool("check_availability_cal")}
                    >
                      Check Availability (Cal.com)
                    </MenuItem>
                    <MenuItem
                      onClick={() => handleAddTool("book_appointment_cal")}
                    >
                      Book Appointment (Cal.com)
                    </MenuItem>
                    <MenuItem onClick={() => handleAddTool("custom")}>
                      Custom Function
                    </MenuItem>
                  </MenuList>
                </Menu>
              </HStack>
              <Text fontSize="xs" color={softText} mb={2}>
                Enable this state with capabilities such as calendar bookings,
                call termination, or your own custom functions.
              </Text>

              <Box>
                {tools.length > 0 ? (
                  <VStack spacing={2} align="stretch">
                    {tools.map((tool, index) => (
                      <Box
                        key={index}
                        p={3}
                        borderWidth="1px"
                        borderRadius="md"
                        mb={3}
                        bg={subtleBg}
                        borderColor={borderColor}
                      >
                        <HStack justify="space-between" mb={2}>
                          <Flex align="center">
                            {tool.type === "transfer_call" && (
                              <VscCallOutgoing
                                size={16}
                                style={{ marginRight: "8px" }}
                              />
                            )}
                            <Text fontWeight="bold">
                              {tool.type
                                .split("_")
                                .map(
                                  (w: string) =>
                                    w.charAt(0).toUpperCase() + w.slice(1)
                                )
                                .join(" ")}
                            </Text>
                          </Flex>
                          <IconButton
                            aria-label="Delete tool"
                            icon={<Trash2 size={16} />}
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteTool(index)}
                          />
                        </HStack>

                        <VStack spacing={3} align="stretch">
                          <FormControl isInvalid={!!toolNameErrors[index]}>
                            <FormLabel fontSize="sm" color="secondary.700">
                              Name
                            </FormLabel>
                            <Input
                              value={tool.name}
                              onChange={(e) =>
                                handleUpdateTool(index, "name", e.target.value)
                              }
                              size="sm"
                              placeholder="Enter Tool Name"
                              {...sharedInputProps}
                              color={textColor}
                              borderColor={borderColor}
                              _focus={{
                                borderColor: "primary.500",
                                boxShadow:
                                  "0 0 0 1px var(--chakra-colors-primary-500)",
                              }}
                            />
                            {toolNameErrors[index] && (
                              <Text color="red.500" fontSize="xs" mt={1}>
                                {toolNameErrors[index]}
                              </Text>
                            )}
                          </FormControl>

                          <FormControl>
                            <FormLabel fontSize="sm" color="secondary.700">
                              Description (Optional)
                            </FormLabel>
                            <Textarea
                              value={tool.description || ""}
                              onChange={(e) =>
                                handleUpdateTool(
                                  index,
                                  "description",
                                  e.target.value
                                )
                              }
                              size="sm"
                              placeholder="Describe what the tool does"
                              {...sharedInputProps}
                              color={textColor}
                              borderColor={borderColor}
                              _focus={{
                                borderColor: "primary.500",
                                boxShadow:
                                  "0 0 0 1px var(--chakra-colors-primary-500)",
                              }}
                            />
                          </FormControl>

                          {/* transfer_call */}
                          {tool.type === "transfer_call" && (
                            <>
                              <FormControl isRequired>
                                <FormLabel fontSize="sm" color="secondary.700">
                                  Transfer to
                                </FormLabel>
                                <RadioGroup
                                  value={
                                    tool.transfer_destination?.type ||
                                    "predefined"
                                  }
                                  onChange={(value) =>
                                    handleUpdateTool(
                                      index,
                                      "transfer_destination.type",
                                      value
                                    )
                                  }
                                  colorScheme="primary"
                                >
                                  <HStack spacing={4}>
                                    <Radio value="predefined">
                                      Static Number
                                    </Radio>
                                    <Radio value="inferred">
                                      Dynamic Routing
                                    </Radio>
                                  </HStack>
                                </RadioGroup>

                                {tool.transfer_destination?.type ===
                                  "predefined" ||
                                !tool.transfer_destination?.type ? (
                                  <Input
                                    mt={2}
                                    value={
                                      tool.transfer_destination?.number || ""
                                    }
                                    onChange={(e) =>
                                      handleUpdateTool(
                                        index,
                                        "transfer_destination.number",
                                        e.target.value
                                      )
                                    }
                                    placeholder="+12134447777"
                                    size="sm"
                                    {...sharedInputProps}
                                    color={textColor}
                                    borderColor={borderColor}
                                    _focus={{
                                      borderColor: "primary.500",
                                      boxShadow:
                                        "0 0 0 1px var(--chakra-colors-primary-500)",
                                    }}
                                  />
                                ) : (
                                  <Textarea
                                    mt={2}
                                    value={
                                      tool.transfer_destination?.prompt || ""
                                    }
                                    onChange={(e) =>
                                      handleUpdateTool(
                                        index,
                                        "transfer_destination.prompt",
                                        e.target.value
                                      )
                                    }
                                    placeholder="Enter prompt to infer the destination number"
                                    size="sm"
                                    rows={3}
                                    {...sharedInputProps}
                                    color={textColor}
                                    borderColor={borderColor}
                                    _focus={{
                                      borderColor: "primary.500",
                                      boxShadow:
                                        "0 0 0 1px var(--chakra-colors-primary-500)",
                                    }}
                                  />
                                )}
                              </FormControl>

                              <FormControl isRequired>
                                <FormLabel fontSize="sm" color="secondary.700">
                                  Type
                                </FormLabel>
                                <RadioGroup
                                  value={
                                    tool.transfer_option?.type ===
                                    "warm_transfer"
                                      ? "warm"
                                      : "cold"
                                  }
                                  onChange={(value) =>
                                    handleUpdateTool(
                                      index,
                                      "transfer_option.type",
                                      value === "warm"
                                        ? "warm_transfer"
                                        : "cold_transfer"
                                    )
                                  }
                                  colorScheme="primary"
                                >
                                  <HStack spacing={4}>
                                    <Radio value="cold">
                                      Cold Transfer
                                      <Tooltip label="AI transfers the call to the next agent without a debrief.">
                                        <IconButton
                                          aria-label="Cold transfer info"
                                          icon={<Info size={14} />}
                                          size="xs"
                                          variant="ghost"
                                          ml={1}
                                        />
                                      </Tooltip>
                                    </Radio>
                                    <Radio value="warm">
                                      Warm Transfer
                                      <Tooltip label="AI provides a debrief to the next agent after transferring the call.">
                                        <IconButton
                                          aria-label="Warm transfer info"
                                          icon={<Info size={14} />}
                                          size="xs"
                                          variant="ghost"
                                          ml={1}
                                        />
                                      </Tooltip>
                                    </Radio>
                                  </HStack>
                                </RadioGroup>
                              </FormControl>

                              {tool.transfer_option?.type ===
                                "cold_transfer" && (
                                <FormControl>
                                  <FormLabel
                                    fontSize="sm"
                                    color="secondary.700"
                                  >
                                    Displayed Phone Number
                                  </FormLabel>
                                  <RadioGroup
                                    value={
                                      tool.transfer_option
                                        ?.show_transferee_as_caller
                                        ? "transferee"
                                        : "retell"
                                    }
                                    onChange={(value) =>
                                      handleUpdateTool(
                                        index,
                                        "transfer_option.show_transferee_as_caller",
                                        value === "transferee"
                                      )
                                    }
                                    colorScheme="primary"
                                  >
                                    <HStack spacing={4}>
                                      <Radio value="retell">
                                        Agent's Number
                                      </Radio>
                                      <Radio value="transferee">
                                        Transferee's Number
                                        <Tooltip label="Original Caller or Callee. If you use custom telephony, enable SIP REFER & PSTN transfer.">
                                          <IconButton
                                            aria-label="Transferee info"
                                            icon={<Info size={14} />}
                                            size="xs"
                                            variant="ghost"
                                            ml={1}
                                          />
                                        </Tooltip>
                                      </Radio>
                                    </HStack>
                                  </RadioGroup>
                                </FormControl>
                              )}

                              {tool.transfer_option?.type ===
                                "warm_transfer" && (
                                <FormControl isRequired>
                                  <FormLabel
                                    fontSize="sm"
                                    color="secondary.700"
                                  >
                                    Handoff Message
                                  </FormLabel>
                                  <RadioGroup
                                    value={
                                      tool.transfer_option
                                        ?.public_handoff_option?.type ||
                                      "static_message"
                                    }
                                    onChange={(value) =>
                                      handleUpdateTool(
                                        index,
                                        "transfer_option.public_handoff_option.type",
                                        value
                                      )
                                    }
                                    colorScheme="primary"
                                  >
                                    <HStack spacing={4}>
                                      <Radio value="static_message">
                                        Static Sentence
                                      </Radio>
                                      <Radio value="prompt">Prompt</Radio>
                                    </HStack>
                                  </RadioGroup>

                                  {tool.transfer_option?.public_handoff_option
                                    ?.type === "static_message" ||
                                  !tool.transfer_option?.public_handoff_option
                                    ?.type ? (
                                    <Textarea
                                      mt={2}
                                      value={
                                        tool.transfer_option
                                          ?.public_handoff_option?.message || ""
                                      }
                                      onChange={(e) =>
                                        handleUpdateTool(
                                          index,
                                          "transfer_option.public_handoff_option.message",
                                          e.target.value
                                        )
                                      }
                                      placeholder="Enter static message"
                                      size="sm"
                                      rows={3}
                                      {...sharedInputProps}
                                      color={textColor}
                                      borderColor={borderColor}
                                      _focus={{
                                        borderColor: "primary.500",
                                        boxShadow:
                                          "0 0 0 1px var(--chakra-colors-primary-500)",
                                      }}
                                    />
                                  ) : (
                                    <Textarea
                                      mt={2}
                                      value={
                                        tool.transfer_option
                                          ?.public_handoff_option?.prompt || ""
                                      }
                                      onChange={(e) =>
                                        handleUpdateTool(
                                          index,
                                          "transfer_option.public_handoff_option.prompt",
                                          e.target.value
                                        )
                                      }
                                      placeholder="Say hello to the agent and summarize the user problem to him"
                                      size="sm"
                                      rows={3}
                                      {...sharedInputProps}
                                      color={textColor}
                                      borderColor={borderColor}
                                      _focus={{
                                        borderColor: "primary.500",
                                        boxShadow:
                                          "0 0 0 1px var(--chakra-colors-primary-500)",
                                      }}
                                    />
                                  )}
                                </FormControl>
                              )}

                              <FormControl>
                                <FormLabel fontSize="sm" color="secondary.700">
                                  Custom SIP Headers
                                </FormLabel>
                                <Text fontSize="xs" color={softText} mb={2}>
                                  Add key/value pairs for call routing,
                                  metadata, or carrier integration.
                                </Text>

                                {tool.custom_sip_headers &&
                                tool.custom_sip_headers.length > 0
                                  ? tool.custom_sip_headers.map(
                                      (header: any, headerIndex: number) => (
                                        <VStack
                                          key={headerIndex}
                                          spacing={1}
                                          mb={3}
                                          align="stretch"
                                        >
                                          <HStack spacing={2}>
                                            <FormControl
                                              isInvalid={!!header.error}
                                              flex={1}
                                            >
                                              <Input
                                                placeholder="Key"
                                                value={header.key}
                                                onChange={(e) =>
                                                  handleUpdateSipHeader(
                                                    index,
                                                    headerIndex,
                                                    "key",
                                                    e.target.value
                                                  )
                                                }
                                                size="sm"
                                                {...sharedInputProps}
                                                color={textColor}
                                                borderColor={borderColor}
                                                _focus={{
                                                  borderColor: "primary.500",
                                                  boxShadow:
                                                    "0 0 0 1px var(--chakra-colors-primary-500)",
                                                }}
                                              />
                                            </FormControl>
                                            <Input
                                              placeholder="Value"
                                              value={header.value}
                                              onChange={(e) =>
                                                handleUpdateSipHeader(
                                                  index,
                                                  headerIndex,
                                                  "value",
                                                  e.target.value
                                                )
                                              }
                                              size="sm"
                                              flex={1}
                                              {...sharedInputProps}
                                              color={textColor}
                                              borderColor={borderColor}
                                              _focus={{
                                                borderColor: "primary.500",
                                                boxShadow:
                                                  "0 0 0 1px var(--chakra-colors-primary-500)",
                                              }}
                                            />
                                            <IconButton
                                              aria-label="Remove header"
                                              icon={<Trash2 size={16} />}
                                              size="sm"
                                              variant="ghost"
                                              onClick={() =>
                                                handleRemoveSipHeader(
                                                  index,
                                                  headerIndex
                                                )
                                              }
                                              flexShrink={0}
                                            />
                                          </HStack>
                                          {header.error && (
                                            <Text
                                              color="red.500"
                                              fontSize="xs"
                                              ml={0}
                                            >
                                              {header.error}
                                            </Text>
                                          )}
                                        </VStack>
                                      )
                                    )
                                  : null}

                                <Button
                                  leftIcon={
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="16"
                                      height="16"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    >
                                      <path d="M5 12h14" />
                                      <path d="M12 5v14" />
                                    </svg>
                                  }
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleAddSipHeader(index)}
                                >
                                  Add
                                </Button>
                              </FormControl>
                            </>
                          )}

                          {tool.type === "end_call" && (
                            <Text fontSize="sm" color={softText}>
                              This tool will end the current call.
                            </Text>
                          )}

                          {tool.type === "press_digit" && (
                            <FormControl>
                              <FormLabel fontSize="sm" color="secondary.700">
                                Delay (ms)
                              </FormLabel>
                              <Input
                                type="number"
                                value={tool.delay_ms || 1000}
                                onChange={(e) => {
                                  const value = Number.parseInt(e.target.value);
                                  const validValue = Math.min(
                                    Math.max(0, value),
                                    5000
                                  );
                                  handleUpdateTool(
                                    index,
                                    "delay_ms",
                                    validValue
                                  );
                                }}
                                placeholder="Delay in milliseconds (0-5000)"
                                size="sm"
                                min={0}
                                max={5000}
                                {...sharedInputProps}
                                color={textColor}
                                borderColor={borderColor}
                                _focus={{
                                  borderColor: "primary.500",
                                  boxShadow:
                                    "0 0 0 1px var(--chakra-colors-primary-500)",
                                }}
                              />
                              <Text fontSize="xs" color={softText} mt={1}>
                                Delay before pressing the digit (0-5000ms).
                                Default: 1000ms (1s)
                              </Text>
                            </FormControl>
                          )}

                          {(tool.type === "check_availability_cal" ||
                            tool.type === "book_appointment_cal") && (
                            <>
                              <FormControl>
                                <FormLabel fontSize="sm" color="secondary.700">
                                  API Key (Cal.com)
                                </FormLabel>
                                <Input
                                  value={tool.cal_api_key || ""}
                                  onChange={(e) =>
                                    handleUpdateTool(
                                      index,
                                      "cal_api_key",
                                      e.target.value
                                    )
                                  }
                                  placeholder="Enter your Cal.com API key"
                                  size="sm"
                                  {...sharedInputProps}
                                  color={textColor}
                                  borderColor={borderColor}
                                  _focus={{
                                    borderColor: "primary.500",
                                    boxShadow:
                                      "0 0 0 1px var(--chakra-colors-primary-500)",
                                  }}
                                />
                              </FormControl>
                              <FormControl>
                                <FormLabel fontSize="sm" color="secondary.700">
                                  Event Type ID (Cal.com)
                                </FormLabel>
                                <Input
                                  type="number"
                                  value={tool.event_type_id || ""}
                                  onChange={(e) =>
                                    handleUpdateTool(
                                      index,
                                      "event_type_id",
                                      Number(e.target.value)
                                    )
                                  }
                                  placeholder="Enter event type ID"
                                  size="sm"
                                  {...sharedInputProps}
                                  color={textColor}
                                  borderColor={borderColor}
                                  _focus={{
                                    borderColor: "primary.500",
                                    boxShadow:
                                      "0 0 0 1px var(--chakra-colors-primary-500)",
                                  }}
                                />
                              </FormControl>
                              <FormControl>
                                <FormLabel fontSize="sm" color="secondary.700">
                                  Timezone (Optional)
                                </FormLabel>
                                <Input
                                  value={tool.timezone || "America/Los_Angeles"}
                                  onChange={(e) =>
                                    handleUpdateTool(
                                      index,
                                      "timezone",
                                      e.target.value
                                    )
                                  }
                                  placeholder="Enter timezone"
                                  size="sm"
                                  {...sharedInputProps}
                                  color={textColor}
                                  borderColor={borderColor}
                                  _focus={{
                                    borderColor: "primary.500",
                                    boxShadow:
                                      "0 0 0 1px var(--chakra-colors-primary-500)",
                                  }}
                                />
                              </FormControl>
                            </>
                          )}

                          {tool.type === "custom" && (
                            <>
                              <FormControl isRequired>
                                <FormLabel fontSize="sm" color="secondary.700">
                                  Your URL
                                </FormLabel>
                                <Input
                                  value={tool.url || ""}
                                  onChange={(e) =>
                                    handleUpdateTool(
                                      index,
                                      "url",
                                      e.target.value
                                    )
                                  }
                                  placeholder="https://your-api-endpoint.com/function"
                                  size="sm"
                                  {...sharedInputProps}
                                  color={textColor}
                                  borderColor={borderColor}
                                  _focus={{
                                    borderColor: "primary.500",
                                    boxShadow:
                                      "0 0 0 1px var(--chakra-colors-primary-500)",
                                  }}
                                />
                              </FormControl>

                              <FormControl>
                                <FormLabel fontSize="sm" color="secondary.700">
                                  Parameters (Optional)
                                </FormLabel>
                                <Text fontSize="xs" color={softText} mb={2}>
                                  JSON schema that defines the format in which
                                  the LLM will return. Please refer to the docs.
                                </Text>
                                <Textarea
                                  value={tool.parameters || ""}
                                  onChange={(e) =>
                                    handleUpdateTool(
                                      index,
                                      "parameters",
                                      e.target.value
                                    )
                                  }
                                  placeholder="Enter JSON Schema here..."
                                  size="sm"
                                  rows={6}
                                  fontFamily="monospace"
                                  {...sharedInputProps}
                                  color={textColor}
                                  borderColor={borderColor}
                                  _focus={{
                                    borderColor: "primary.500",
                                    boxShadow:
                                      "0 0 0 1px var(--chakra-colors-primary-500)",
                                  }}
                                />
                                {jsonSchemaError[index] && (
                                  <Text color="red.500" fontSize="xs" mt={1}>
                                    {jsonSchemaError[index]}
                                  </Text>
                                )}
                                <HStack mt={2} spacing={2}>
                                  <Button
                                    variant="outline"
                                    size="xs"
                                    onClick={() =>
                                      handleUpdateTool(
                                        index,
                                        "parameters",
                                        JSON.stringify(
                                          exampleSchemas[0],
                                          null,
                                          2
                                        )
                                      )
                                    }
                                  >
                                    example 1
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="xs"
                                    onClick={() =>
                                      handleUpdateTool(
                                        index,
                                        "parameters",
                                        JSON.stringify(
                                          exampleSchemas[1],
                                          null,
                                          2
                                        )
                                      )
                                    }
                                  >
                                    example 2
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="xs"
                                    onClick={() =>
                                      handleUpdateTool(
                                        index,
                                        "parameters",
                                        JSON.stringify(
                                          exampleSchemas[2],
                                          null,
                                          2
                                        )
                                      )
                                    }
                                  >
                                    example 3
                                  </Button>
                                </HStack>
                                <Button
                                  mt={2}
                                  size="sm"
                                  width="100%"
                                  variant="outline"
                                  onClick={() => formatJsonSchema(index)}
                                >
                                  Format JSON
                                </Button>
                              </FormControl>

                              <FormControl>
                                <Checkbox
                                  isChecked={tool.speak_during_execution}
                                  onChange={(e) =>
                                    handleUpdateTool(
                                      index,
                                      "speak_during_execution",
                                      e.target.checked
                                    )
                                  }
                                  size="sm"
                                  mt={2}
                                  colorScheme="primary"
                                >
                                  <Text fontSize="sm" fontWeight="medium">
                                    Speak During Execution
                                  </Text>
                                </Checkbox>
                                <Text
                                  fontSize="xs"
                                  color={softText}
                                  ml={6}
                                  mt={1}
                                >
                                  If the function takes over 2 seconds, the
                                  agent can say something like: "Let me check
                                  that for you."
                                </Text>

                                {tool.speak_during_execution && (
                                  <Textarea
                                    mt={2}
                                    value={
                                      tool.execution_message_description || ""
                                    }
                                    onChange={(e) =>
                                      handleUpdateTool(
                                        index,
                                        "execution_message_description",
                                        e.target.value
                                      )
                                    }
                                    placeholder="The message you will say to user when calling this tool. Make sure it fits into the conversation smoothly."
                                    size="sm"
                                    rows={3}
                                    {...sharedInputProps}
                                    color={textColor}
                                    borderColor={borderColor}
                                    _focus={{
                                      borderColor: "primary.500",
                                      boxShadow:
                                        "0 0 0 1px var(--chakra-colors-primary-500)",
                                    }}
                                  />
                                )}
                              </FormControl>

                              <FormControl>
                                <Checkbox
                                  isChecked={tool.speak_after_execution}
                                  onChange={(e) =>
                                    handleUpdateTool(
                                      index,
                                      "speak_after_execution",
                                      e.target.checked
                                    )
                                  }
                                  size="sm"
                                  mt={2}
                                  colorScheme="primary"
                                >
                                  <Text fontSize="sm" fontWeight="medium">
                                    Speak After Execution
                                  </Text>
                                </Checkbox>
                                <Text
                                  fontSize="xs"
                                  color={softText}
                                  ml={6}
                                  mt={1}
                                >
                                  Unselect if you want to run the function
                                  silently, such as uploading the call result to
                                  the server silently.
                                </Text>
                              </FormControl>

                              <FormControl>
                                <FormLabel fontSize="sm" color="secondary.700">
                                  Timeout (ms)
                                </FormLabel>
                                <Input
                                  type="number"
                                  value={tool.timeout_ms || 120000}
                                  onChange={(e) => {
                                    const value = Number.parseInt(
                                      e.target.value
                                    );
                                    const validValue = Math.min(
                                      Math.max(1000, value),
                                      600000
                                    );
                                    handleUpdateTool(
                                      index,
                                      "timeout_ms",
                                      validValue
                                    );
                                  }}
                                  placeholder="Timeout in milliseconds (1000-600000)"
                                  size="sm"
                                  min={1000}
                                  max={600000}
                                  {...sharedInputProps}
                                  color={textColor}
                                  borderColor={borderColor}
                                  _focus={{
                                    borderColor: "primary.500",
                                    boxShadow:
                                      "0 0 0 1px var(--chakra-colors-primary-500)",
                                  }}
                                />
                                <Text fontSize="xs" color={softText} mt={1}>
                                  Maximum execution time (1000-600000ms).
                                  Default: 120000ms (2min)
                                </Text>
                              </FormControl>
                            </>
                          )}
                        </VStack>
                      </Box>
                    ))}
                  </VStack>
                ) : (
                  <Text
                    fontSize="xs"
                    color={softText}
                    textAlign="center"
                    py={4}
                  >
                    No tools added yet. Click "Add Tool" to add a tool.
                  </Text>
                )}
              </Box>
            </Box>

            <Divider mt={8} borderColor={borderColor} />

            <Box>
              <Checkbox
                isChecked={enableInterruptionSensitivity}
                onChange={(e) =>
                  setEnableInterruptionSensitivity(e.target.checked)
                }
                size="sm"
                colorScheme="primary"
                mt={2}
              >
                <Text fontSize="sm" fontWeight="medium">
                  Enable State Interruption Sensitivity
                </Text>
              </Checkbox>

              {enableInterruptionSensitivity && (
                <Box mt={2}>
                  <Text fontSize="sm" color={softText} mb={1}>
                    Interruption Sensitivity (
                    {interruptionSensitivityValue.toFixed(2)})
                  </Text>
                  <Text fontSize="xs" color={softText} mb={2}>
                    Override the default interruption sensitivity for this state
                  </Text>
                  <HStack spacing={4}>
                    <Text fontSize="xs" color={softText}>
                      Low
                    </Text>
                    <Slider
                      aria-label="Interruption Sensitivity"
                      min={0}
                      max={1}
                      step={0.01}
                      value={interruptionSensitivityValue}
                      onChange={(value) =>
                        setInterruptionSensitivityValue(value)
                      }
                      flex={1}
                      colorScheme="primary"
                    >
                      <SliderTrack>
                        <SliderFilledTrack />
                      </SliderTrack>
                      <SliderThumb boxSize={4} />
                    </Slider>
                    <Text fontSize="xs" color={softText}>
                      High
                    </Text>
                  </HStack>
                </Box>
              )}
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            bg="primary.500"
            color="white"
            _hover={{ bg: "primary.600" }}
            onClick={handleSave}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
